/**
 * Générateur — Automatisme : dire si des figures planes sont images l'une de
 * l'autre par une symétrie axiale (dont on identifie l'axe) ou par un
 * demi-tour (dont on identifie le centre).
 * Attendu ID : 1120 | Niveau : 4ème | Domaine : Transformations
 * Rubrique : AUTOMATISME (programme 2026, Parallélogrammes et translations 4e).
 * Labels (cartes exerciseur) :
 *   - Quelle transformation ?        (QCM sur figure : symétrie axiale, demi-tour, ni l'une ni l'autre)
 *   - Identifier l'axe               (QCM sur figure : quatre droites tracées)
 *   - Identifier le centre           (QCM sur figure : quatre points)
 *   - Vrai ou faux ?                 (V/F sur figure avec une droite et un point tracés)
 *   - Symétries : vrai ou faux ?     (V/F de pur rappel)
 *
 * ── Plan de cartes validé (2026-09-09) ─────────────────────────────────────
 * Source : BO cycle 4 (2026), Parallélogrammes et translations 4e + grille
 * P1-P12. P1 : CM2 introduit (figures symétriques, axe), 6e consolidé
 * (symétrique d'une figure, axes de symétrie), 5e étendu (demi-tour défini),
 * 4e automatisme. P2 : sens installés — pliage / miroir pour la symétrie
 * axiale, « tête en bas » pour le demi-tour, axe médiatrice et centre
 * milieu ; non installés (distracteurs) — la translation (« ni l'une ni
 * l'autre »), la rotation quelconque, « superposables donc symétriques ».
 * P3 : quadrillage de −5 à 5, sommets entiers, axes verticaux, horizontaux
 * ou à 45°, centres entiers. P5 : quadrillage. P6 : aucun instrument. P7 : la
 * translation n'est pas nommée. P8 : symétrie axiale, axe, demi-tour,
 * centre, image. P9 : symétrie axiale sur quadrillage (5e), symétrique par
 * demi-tour (1119), milieu (6e). P10 : constat. P11 : superposables ≠
 * symétriques ; miroir ≠ tête en bas ; axe ou centre mal placés. P12 :
 * rémanence forte.
 * INVARIANTS :
 *   - les formes n'ont aucune symétrie propre → une seule transformation
 *     relie F et G ; F et G ne se chevauchent pas et restent dans la grille ;
 *   - toute figure porte data-transfo / data-f / data-g pour les solveurs ;
 *   - V/F : pas de crochets, mixité 1-3 faux, familles exclusives, fenêtre
 *     stricte.
 */
import {
    randomChoice, randInt, shuffle,
    makeQcm, makeVraiFaux,
    retirerCodesErreur,
} from '../../../utils';
import { imgElement } from '../../5eme/statistiques/svgStats';

const ATTENDU_ID = 1120;
const opts = (label) => ({ niveau: '4eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_QUELLE = 'Quelle transformation ?';
const L_AXE = 'Identifier l\'axe';
const L_CENTRE = 'Identifier le centre';
const L_VF = 'Vrai ou faux ?';
const L_RAPPEL = 'Symétries : vrai ou faux ?';

const B = 5;
const OPTIONS = { axiale: 'une symétrie axiale', demitour: 'un demi-tour', aucune: 'ni l\'une ni l\'autre' };

// ─── Formes SANS symétrie propre (sommets entiers, coin en (0,0)) ──────────
// Revue 2026-09-09 : le L à branches égales était symétrique par sa diagonale et le
// drapeau à hampe centrée par l'horizontale → l'image par un axe était aussi une
// translation ou un demi-tour (réponse non unique). Branches 3 et 4, drapeau en bas.
const FORMES = {
    L: [[0, 0], [3, 0], [3, 1], [1, 1], [1, 4], [0, 4]],
    triangle: [[0, 0], [3, 0], [0, 2]],
    drapeau: [[0, 0], [1, 0], [1, 1], [3, 1], [3, 2], [1, 2], [1, 4], [0, 4]],
};
const boite = (pts) => ({ x0: Math.min(...pts.map((p) => p[0])), x1: Math.max(...pts.map((p) => p[0])), y0: Math.min(...pts.map((p) => p[1])), y1: Math.max(...pts.map((p) => p[1])) });
const dans = (pts) => pts.every(([x, y]) => Math.abs(x) <= B && Math.abs(y) <= B);
const disjoints = (a, b) => { const A = boite(a); const C = boite(b); return A.x1 < C.x0 || C.x1 < A.x0 || A.y1 < C.y0 || C.y1 < A.y0; };
const translate = (pts, tx, ty) => pts.map(([x, y]) => [x + tx, y + ty]);

// ─── Transformations ───────────────────────────────────────────────────────
// Droite : { type: 'v' | 'h' | 'd' | 'a', c } — v : x = c ; h : y = c ; d : y = x + c ; a : y = −x + c
const reflechir = (pts, dr) => pts.map(([x, y]) => (dr.type === 'v' ? [2 * dr.c - x, y] : dr.type === 'h' ? [x, 2 * dr.c - y] : dr.type === 'd' ? [y - dr.c, x + dr.c] : [dr.c - y, dr.c - x]));
const demiTour = (pts, O) => pts.map(([x, y]) => [2 * O.x - x, 2 * O.y - y]);
const memeDroite = (a, b) => a.type === b.type && a.c === b.c;

/** Une paire (F, G) reliée par `transfo`, avec l'axe ou le centre, toutes deux dans la grille et disjointes. */
const tirerPaire = (transfo = randomChoice(['axiale', 'demitour', 'aucune'])) => {
    // la forme est tirée UNE fois (revue 2026-09-09 : retirée à chaque essai, le triangle, plus petit, sortait 57 % du temps)
    const nom = randomChoice(Object.keys(FORMES)); const forme = FORMES[nom];
    // idem pour le type d'axe (les axes horizontaux sortaient 19 % du temps contre 35 % pour les verticaux)
    const typeAxe = randomChoice(['v', 'h', 'd', 'a']);
    for (let essai = 0; essai < 400; essai++) {
        const F = translate(forme, randInt(-5, 2), randInt(-5, 2)); if (!dans(F)) continue;
        let G; let axe = null; let centre = null;
        if (transfo === 'axiale') { axe = { type: typeAxe, c: randInt(-3, 3) }; G = reflechir(F, axe); }
        else if (transfo === 'demitour') { centre = { x: randInt(-2, 2), y: randInt(-2, 2) }; G = demiTour(F, centre); }
        else { G = translate(F, randInt(-6, 6), randInt(-6, 6)); }
        if (!dans(G) || !disjoints(F, G)) continue;
        return { transfo, nom, F, G, axe, centre };
    }
    return tirerPaire(transfo);
};

// ─── Quadrillage SVG ───────────────────────────────────────────────────────
const CELL = 22; const PAD = 26; const W = 2 * B * CELL + 2 * PAD;
const px = (x) => PAD + (x + B) * CELL; const py = (y) => PAD + (B - y) * CELL;
const poly = (pts, fill, label) => {
    // étiquette au-dessus du coin haut gauche de la figure (le centre d'un L tombe sur un bord)
    const bb = boite(pts);
    return `<polygon points="${pts.map(([x, y]) => `${px(x)},${py(y)}`).join(' ')}" fill="${fill}" fill-opacity="0.55" stroke="${fill}" stroke-width="1.8" stroke-linejoin="round"/>`
        + `<text x="${(px(bb.x0) + 2).toFixed(1)}" y="${(py(bb.y1) - 5).toFixed(1)}" font-family="system-ui,sans-serif" font-size="14" font-weight="800" fill="${fill}">${label}</text>`;
};
/** Droite tracée sur tout le cadre (pointillés) avec son nom au bord. */
const droite = (dr, nom, couleur = '#64748b', bout = 'b') => {
    let a; let b;
    if (dr.type === 'v') { a = [dr.c, -B - 0.4]; b = [dr.c, B + 0.4]; }
    else if (dr.type === 'h') { a = [-B - 0.4, dr.c]; b = [B + 0.4, dr.c]; }
    else if (dr.type === 'd') { a = [-B - 0.4, -B - 0.4 + dr.c]; b = [B + 0.4, B + 0.4 + dr.c]; }
    else { a = [-B - 0.4, B + 0.4 + dr.c]; b = [B + 0.4, -B - 0.4 + dr.c]; }
    const clip = ([x, y]) => [Math.max(-B - 0.4, Math.min(B + 0.4, x)), Math.max(-B - 0.4, Math.min(B + 0.4, y))];
    a = clip(a); b = clip(b);
    // nom placé à l'extrémité `bout` (b par défaut ; a pour une parallèle voisine, afin que les deux noms ne se touchent pas),
    // selon le bord par lequel la droite sort (haut, bas, gauche ou droite)
    const e = bout === 'a' ? a : b;
    const haut = e[1] >= B + 0.4; const bas = e[1] <= -B - 0.4; const gauche = e[0] <= -B - 0.4;
    const tx = haut || bas ? px(e[0]) : gauche ? px(e[0]) + 4 : px(e[0]) - 4; const ty = haut ? py(e[1]) - 4 : bas ? py(e[1]) + 13 : py(e[1]) - 6;
    return `<line x1="${px(a[0])}" y1="${py(a[1])}" x2="${px(b[0])}" y2="${py(b[1])}" stroke="${couleur}" stroke-width="1.8" stroke-dasharray="6 4"/>`
        + `<text x="${tx.toFixed(1)}" y="${ty.toFixed(1)}" font-family="system-ui,sans-serif" font-size="12" font-weight="700" fill="${couleur}" text-anchor="${haut || bas ? 'middle' : gauche ? 'start' : 'end'}">${nom}</text>`;
};
const point = (P, label, couleur = '#b45309', dec = [7, -7]) => `<circle cx="${px(P.x)}" cy="${py(P.y)}" r="4.5" fill="${couleur}"/><text x="${px(P.x) + dec[0]}" y="${py(P.y) + dec[1]}" font-family="system-ui,sans-serif" font-size="14" font-weight="700" fill="${couleur}">${label}</text>`;
/** Boîte (px) d'une étiquette de 14 px placée en (x, y) avec le décalage dec. */
const boiteEtiquette = (x, y, dec) => ({ x0: x + dec[0], x1: x + dec[0] + 11, y0: y + dec[1] - 12, y1: y + dec[1] + 2 });
const boitesSeCoupent = (a, b) => !(a.x1 < b.x0 || b.x1 < a.x0 || a.y1 < b.y0 || b.y1 < a.y0);
/**
 * Placement des étiquettes de plusieurs points (revue 2026-09-09) : pour chacun, la première des quatre
 * positions (haut-droite, bas-droite, haut-gauche, bas-gauche) qui ne recouvre ni un autre point, ni une
 * étiquette déjà posée, ni le nom d'une figure (coin haut gauche de F et de G).
 */
const pointsEtiquetes = (T, pts, couleur) => {
    const occupes = [T.F, T.G].map((f) => { const bb = boite(f); return { x0: px(bb.x0) + 2, x1: px(bb.x0) + 14, y0: py(bb.y1) - 17, y1: py(bb.y1) - 3 }; });
    pts.forEach((c) => occupes.push({ x0: px(c.p.x) - 5, x1: px(c.p.x) + 5, y0: py(c.p.y) - 5, y1: py(c.p.y) + 5 }));
    const DECS = [[7, -7], [7, 16], [-17, -7], [-17, 16]];
    return pts.map((c) => {
        const x = px(c.p.x); const y = py(c.p.y);
        const dec = DECS.find((d) => { const b = boiteEtiquette(x, y, d); return b.x0 >= 0 && b.x1 <= W && b.y0 >= 0 && b.y1 <= W && !occupes.some((o) => boitesSeCoupent(o, b)); }) || DECS[0];
        occupes.push(boiteEtiquette(x, y, dec));
        return point(c.p, c.label, couleur, dec);
    }).join('');
};
const grille = (T, extra = '', data = {}) => {
    let g = `<rect x="0" y="0" width="${W}" height="${W}" fill="#ffffff"/>`;
    for (let v = -B; v <= B; v++) {
        g += `<line x1="${px(v)}" y1="${py(-B)}" x2="${px(v)}" y2="${py(B)}" stroke="#cbd5e1" stroke-width="1"/>`;
        g += `<line x1="${px(-B)}" y1="${py(v)}" x2="${px(B)}" y2="${py(v)}" stroke="#cbd5e1" stroke-width="1"/>`;
    }
    g += poly(T.F, '#1e3a8a', 'F') + poly(T.G, '#b45309', 'G') + extra;
    const attrs = Object.entries({ 'data-transfo': T.transfo, 'data-f': T.F.map((p) => p.join(';')).join('|'), 'data-g': T.G.map((p) => p.join(';')).join('|'), ...data }).map(([k, v]) => `${k}="${v}"`).join(' ');
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${W}" width="${W}" height="${W}" ${attrs}>${g}</svg>`;
};
const cleDroite = (d) => `${d.type}${d.c}`;
const clePoint = (P) => `${P.x};${P.y}`;
const signature = (T) => `${T.transfo}-${T.nom}-${T.F[0].join(',')}-${T.G[0].join(',')}`;

// ─── Codes d'erreur (contenu.erreurs) ──────────────────────────────────────
/** La confusion entre la transformation vraie et celle que l'élève croit voir (aucune : la figure a seulement glissé). */
const codeConfusion = (vraie, crue) => ({
    'axiale+demitour': 'demi_tour_pour_axiale', 'demitour+axiale': 'demi_tour_pour_axiale',
    'aucune+demitour': 'demi_tour_pour_translation', 'demitour+aucune': 'demi_tour_pour_translation',
    'axiale+aucune': 'translation_pour_axiale', 'aucune+axiale': 'translation_pour_axiale',
}[`${vraie}+${crue}`] || null);

// ─── Anti-répétition ───────────────────────────────────────────────────────
const antiRepeat = (construire, ref) => {
    let payload = construire();
    for (let t = 0; t < 8 && payload._meta.params.signature === ref.v; t++) payload = construire();
    ref.v = payload._meta.params.signature;
    return payload;
};
const refQ = { v: null }; const refA = { v: null }; const refC = { v: null };

// ─── Carte 1 : quelle transformation ? ─────────────────────────────────────

const construireQuelle = () => {
    const T = tirerPaire();
    const bonne = OPTIONS[T.transfo];
    const payload = makeQcm(Object.values(OPTIONS), bonne, '<p>Les figures F et G sont-elles images l\'une de l\'autre par une symétrie axiale ou par un demi-tour ?</p>', { ...opts(L_QUELLE), elements_additionnels: imgElement(grille(T), 'medium') });
    payload.contenu = { ...payload.contenu, sousType: T.transfo };
    payload._meta.params = { transfo: T.transfo, nom: T.nom, F: T.F, G: T.G, signature: signature(T) };
    // chaque choix faux nommé par la confusion entre la transformation vraie et celle qu'il dit
    const erreurs = Object.keys(OPTIONS).filter((t) => t !== T.transfo).map((t) => ({ code: codeConfusion(T.transfo, t), valeur: OPTIONS[t] })).filter((x) => x.code);
    if (erreurs.length) payload.contenu = { ...payload.contenu, erreurs };
    return payload;
};
const genQuelle = () => antiRepeat(construireQuelle, refQ);

// ─── Carte 2 : identifier l'axe (QCM parmi quatre droites) ─────────────────

const construireAxe = () => {
    for (let essai = 0; essai < 100; essai++) {
        const T = tirerPaire('axiale'); const axe = T.axe;
        const perp = { v: 'h', h: 'v', d: 'a', a: 'd' }[axe.type];
        // milieu de la paire (centres des boîtes) pour placer la perpendiculaire et la diagonale leurres
        const bF = boite(T.F); const bG = boite(T.G); const mx = (bF.x0 + bF.x1 + bG.x0 + bG.x1) / 4; const my = (bF.y0 + bF.y1 + bG.y0 + bG.y1) / 4;
        const cPerp = perp === 'v' ? Math.round(mx) : perp === 'h' ? Math.round(my) : perp === 'd' ? Math.round(my - mx) : Math.round(my + mx);
        const autreType = randomChoice(['v', 'h', 'd', 'a'].filter((t) => t !== axe.type && t !== perp));
        const cAutre = autreType === 'v' ? Math.round(mx) : autreType === 'h' ? Math.round(my) : autreType === 'd' ? Math.round(my - mx) : Math.round(my + mx);
        const cands = [
            { d: axe, role: 'axe' },
            { d: { type: axe.type, c: axe.c + randomChoice([-1, 1]) }, role: 'parallele_decalee' },
            { d: { type: perp, c: cPerp }, role: 'perpendiculaire' },
            { d: { type: autreType, c: cAutre }, role: 'autre_direction' },
        ];
        const pris = []; cands.forEach((c) => { if (Math.abs(c.d.c) <= B && !pris.some((q) => memeDroite(q.d, c.d))) pris.push(c); });
        if (pris.length < 4) continue;
        const noms = shuffle(['(d1)', '(d2)', '(d3)', '(d4)']);
        const droites = pris.map((c, i) => ({ ...c, nom: noms[i] }));
        const bonne = droites.find((c) => c.role === 'axe').nom;
        const couleurs = { '(d1)': '#0f766e', '(d2)': '#7c3aed', '(d3)': '#dc2626', '(d4)': '#475569' };
        const extra = droites.map((c) => droite(c.d, c.nom, couleurs[c.nom], c.role === 'parallele_decalee' ? 'a' : 'b')).join('');
        const svg = grille(T, extra, { 'data-droites': droites.map((c) => `${c.nom}:${cleDroite(c.d)}`).join('|') });
        const payload = makeQcm(['(d1)', '(d2)', '(d3)', '(d4)'], bonne, '<p>G est l\'image de F par une symétrie axiale. Quelle droite est l\'axe de cette symétrie ?</p>', { ...opts(L_AXE), elements_additionnels: imgElement(svg, 'medium') });
        payload.contenu.choix = ['(d1)', '(d2)', '(d3)', '(d4)']; payload.contenu.bonne_reponse = 'ABCD'[['(d1)', '(d2)', '(d3)', '(d4)'].indexOf(bonne)];
        payload.contenu = { ...payload.contenu, sousType: axe.type };
        payload._meta.params = { transfo: 'axiale', axe, droites: droites.map((c) => ({ nom: c.nom, role: c.role, d: c.d })), F: T.F, G: T.G, signature: `${signature(T)}-${cleDroite(axe)}` };
        // seule la parallèle décalée est une erreur nommée (la perpendiculaire et l'autre direction n'en portent pas)
        const erreurs = droites.filter((c) => c.role === 'parallele_decalee').map((c) => ({ code: 'axe_ou_centre_decale', valeur: c.nom }));
        if (erreurs.length) payload.contenu = { ...payload.contenu, erreurs };
        return payload;
    }
    throw new Error('gen_1120 : axe introuvable');
};
const genAxe = () => antiRepeat(construireAxe, refA);

// ─── Carte 3 : identifier le centre (QCM parmi quatre points) ──────────────

const construireCentre = () => {
    for (let essai = 0; essai < 100; essai++) {
        const T = tirerPaire('demitour'); const O = T.centre;
        // jamais le coin haut gauche, où est écrit le nom de la figure (revue 2026-09-09)
        const sansCoin = (f) => { const bb = boite(f); return f.filter(([x, y]) => !(x === bb.x0 && y === bb.y1)); };
        const sommetF = randomChoice(sansCoin(T.F)); const sommetG = randomChoice(sansCoin(T.G));
        const cands = [
            { p: O, role: 'centre' },
            { p: { x: O.x + randomChoice([-1, 1]), y: O.y + randomChoice([-1, 0, 1]) }, role: 'decale' },
            { p: { x: sommetF[0], y: sommetF[1] }, role: 'sommet_f' },
            { p: { x: sommetG[0], y: sommetG[1] }, role: 'sommet_g' },
        ];
        const pris = []; cands.forEach((c) => { if (Math.abs(c.p.x) <= B && Math.abs(c.p.y) <= B && !pris.some((q) => clePoint(q.p) === clePoint(c.p))) pris.push(c); });
        if (pris.length < 4) continue;
        const lettres = shuffle(['A', 'B', 'C', 'D']);
        const points = pris.map((c, i) => ({ ...c, label: lettres[i] }));
        const bonne = points.find((c) => c.role === 'centre').label;
        const extra = pointsEtiquetes(T, points, '#0f766e');
        const svg = grille(T, extra, { 'data-points': points.map((c) => `${c.label}:${clePoint(c.p)}`).join('|') });
        const payload = makeQcm(['A', 'B', 'C', 'D'], bonne, '<p>G est l\'image de F par un demi-tour. Quel point est le centre de ce demi-tour ?</p>', { ...opts(L_CENTRE), elements_additionnels: imgElement(svg, 'medium') });
        payload.contenu.choix = ['A', 'B', 'C', 'D']; payload.contenu.bonne_reponse = bonne;
        payload.contenu = { ...payload.contenu, sousType: T.nom };
        payload._meta.params = { transfo: 'demitour', centre: O, points: points.map((c) => ({ label: c.label, role: c.role, p: c.p })), F: T.F, G: T.G, signature: `${signature(T)}-${clePoint(O)}` };
        // un sommet de F ou de G : le centre pris sur la figure ; un point voisin du centre : centre décalé ; jamais les deux à la fois
        const sommets = [...T.F, ...T.G].map(([x, y]) => clePoint({ x, y }));
        const codePoint = (p) => {
            const c = new Set();
            if (Math.max(Math.abs(p.x - O.x), Math.abs(p.y - O.y)) === 1) c.add('axe_ou_centre_decale');
            if (sommets.includes(clePoint(p))) c.add('axe_ou_centre_dans_la_figure');
            return c.size === 1 ? [...c][0] : null;
        };
        const erreurs = points.filter((c) => c.role !== 'centre').map((c) => ({ code: codePoint(c.p), valeur: c.label })).filter((x) => x.code);
        if (erreurs.length) payload.contenu = { ...payload.contenu, erreurs };
        return payload;
    }
    throw new Error('gen_1120 : centre introuvable');
};
const genCentre = () => antiRepeat(construireCentre, refC);

// ─── Carte 4 : vrai ou faux ? (figure avec une droite (d) et un point O) ───

let dernieresLignesVf1120 = [];

const genVf = () => {
    for (let essai = 0; essai < 160; essai++) {
        const T = tirerPaire();
        // droite (d) : l'axe réel (si axiale, 2 fois sur 3), sinon une droite plausible entre les figures ; point O : idem
        const bF = boite(T.F); const bG = boite(T.G); const mx = Math.round((bF.x0 + bF.x1 + bG.x0 + bG.x1) / 4); const my = Math.round((bF.y0 + bF.y1 + bG.y0 + bG.y1) / 4);
        const d = T.transfo === 'axiale' && Math.random() < 0.66 ? T.axe : { type: randomChoice(['v', 'h']), c: randomChoice(['v', 'h']) === 'v' ? mx : my };
        const O = T.transfo === 'demitour' && Math.random() < 0.66 ? T.centre : { x: mx + randomChoice([-1, 0, 1]), y: my + randomChoice([-1, 0, 1]) };
        if (Math.abs(d.c) > B || Math.abs(O.x) > B || Math.abs(O.y) > B) continue;
        const dEstAxe = T.transfo === 'axiale' && memeDroite(d, T.axe);
        const oEstCentre = T.transfo === 'demitour' && clePoint(O) === clePoint(T.centre);
        const candidates = [
            { l: 'G est l\'image de F par la symétrie d\'axe (d).', v: dEstAxe, f: 'axe' },
            { l: 'G est l\'image de F par le demi-tour de centre O.', v: oEstCentre, f: 'centre' },
            { l: 'G est la figure F retournée, comme dans un miroir.', v: T.transfo === 'axiale', f: 'miroir' },
            { l: 'G est la figure F tournée d\'un demi-tour, la tête en bas.', v: T.transfo === 'demitour', f: 'tete' },
            { l: 'Pour passer de F à G, la figure a seulement glissé, sans tourner ni se retourner.', v: T.transfo === 'aucune', f: 'glisse' },
        ];
        const fausses = shuffle(candidates.filter((it) => !it.v)); const vraies = shuffle(candidates.filter((it) => it.v));
        // nombre de faux tiré dans l'intervalle réellement possible (revue 2026-09-09 : la translation, une seule ligne vraie, sortait 22 % du temps)
        const minFaux = Math.max(1, 4 - vraies.length); const maxFaux = Math.min(3, fausses.length);
        if (minFaux > maxFaux) continue;
        const nbFaux = randInt(minFaux, maxFaux);
        const lignes = shuffle([...fausses.slice(0, nbFaux), ...vraies.slice(0, 4 - nbFaux)]);
        if (lignes.length !== 4 || new Set(lignes.map((it) => it.f)).size !== 4) continue;
        if (essai > 120) dernieresLignesVf1120 = [];
        if (lignes.some((it) => dernieresLignesVf1120.includes(it.l))) continue;
        dernieresLignesVf1120 = lignes.map((it) => it.l);
        const svg = grille(T, droite(d, '(d)', '#0f766e') + point(O, 'O'), { 'data-droite': cleDroite(d), 'data-o': clePoint(O) });
        const payload = makeVraiFaux(
            lignes.map((it) => it.l),
            lignes.map((it) => (it.v ? 0 : 1)),
            '<p>Observe les figures F et G, la droite (d) et le point O. Pour chaque affirmation, dis si elle est vraie ou fausse.</p>',
            { ...opts(L_VF), elements_additionnels: imgElement(svg, 'medium') },
        );
        payload.contenu = { ...payload.contenu, cadence: 'raisonnement' };
        payload._meta.params = { transfo: T.transfo, d, O, dEstAxe, oEstCentre, F: T.F, G: T.G };
        // la transformation vraie est connue : chaque ligne fausse dit la transformation crue, ou un axe parallèle ou un centre voisin
        const codeFaux = {
            axe: T.transfo === 'axiale' ? (d.type === T.axe.type ? 'axe_ou_centre_decale' : null) : codeConfusion(T.transfo, 'axiale'),
            centre: T.transfo === 'demitour' ? 'axe_ou_centre_decale' : codeConfusion(T.transfo, 'demitour'),
            miroir: codeConfusion(T.transfo, 'axiale'),
            tete: codeConfusion(T.transfo, 'demitour'),
            glisse: codeConfusion(T.transfo, 'aucune'),
        };
        const erreurs = lignes.filter((it) => !it.v && codeFaux[it.f]).map((it) => ({ code: codeFaux[it.f], ligne: it.l }));
        if (erreurs.length) payload.contenu = { ...payload.contenu, erreurs };
        return payload;
    }
    throw new Error('gen_1120 : V/F sans tirage après 160 essais');
};

// ─── Carte 5 : symétries, vrai ou faux ? (pur rappel) ──────────────────────

const POOL_RAPPEL = [
    { l: 'Par une symétrie axiale, l\'image d\'une figure est retournée, comme par un pliage le long de l\'axe.', v: true, f: 'axiale' },
    { l: 'Par une symétrie axiale, l\'image d\'une figure a seulement glissé.', v: false, f: 'axiale', code: 'translation_pour_axiale' },
    { l: 'Par un demi-tour, l\'image d\'une figure est tournée la tête en bas autour du centre.', v: true, f: 'demitour' },
    { l: 'Par un demi-tour, l\'image d\'une figure est retournée comme dans un miroir.', v: false, f: 'demitour', code: 'demi_tour_pour_axiale' },
    { l: 'L\'axe d\'une symétrie axiale est à égale distance d\'un point et de son image.', v: true, f: 'axe' },
    { l: 'L\'axe d\'une symétrie axiale passe toujours par la figure.', v: false, f: 'axe', code: 'axe_ou_centre_dans_la_figure' },
    { l: 'Le centre d\'un demi-tour est le milieu du segment qui joint un point et son image.', v: true, f: 'centre' },
    { l: 'Le centre d\'un demi-tour est toujours un sommet de la figure.', v: false, f: 'centre', code: 'axe_ou_centre_dans_la_figure' },
    { l: 'Deux figures images l\'une de l\'autre par une symétrie axiale ou un demi-tour sont superposables.', v: true, f: 'superposables' },
    { l: 'Deux figures superposables sont toujours symétriques par rapport à un axe.', v: false, f: 'superposables' },
    { l: 'Une symétrie axiale et un demi-tour ne donnent pas la même image d\'une figure sans symétrie.', v: true, f: 'difference' },
    { l: 'Une symétrie axiale et un demi-tour donnent toujours la même image.', v: false, f: 'difference', code: 'demi_tour_pour_axiale' },
    { l: 'Le demi-tour est aussi appelé symétrie centrale.', v: true, f: 'nom' },
    { l: 'La symétrie axiale est aussi appelée symétrie centrale.', v: false, f: 'nom', code: 'demi_tour_pour_axiale' },
];

let dernieresLignesRappel1120 = [];

const genRappelVf = () => {
    for (let essai = 0; essai < 160; essai++) {
        const fausses = shuffle(POOL_RAPPEL.filter((it) => !it.v)); const vraies = shuffle(POOL_RAPPEL.filter((it) => it.v));
        const nbFaux = randInt(1, 3);
        const lignes = shuffle([...fausses.slice(0, nbFaux), ...vraies.slice(0, 4 - nbFaux)]);
        if (new Set(lignes.map((it) => it.f)).size !== 4) continue;
        if (essai > 120) dernieresLignesRappel1120 = [];
        if (lignes.some((it) => dernieresLignesRappel1120.includes(it.l))) continue;
        dernieresLignesRappel1120 = lignes.map((it) => it.l);
        const payload = makeVraiFaux(
            lignes.map((it) => it.l),
            lignes.map((it) => (it.v ? 0 : 1)),
            '<p>Pour chaque affirmation sur les symétries, dis si elle est vraie ou fausse.</p>',
            opts(L_RAPPEL),
        );
        payload._meta.params = { lignes: lignes.map((it) => POOL_RAPPEL.indexOf(it)) };
        // le code de chaque affirmation fausse du vivier (aucun pour « superposables donc symétriques »)
        const erreurs = lignes.filter((it) => !it.v && it.code).map((it) => ({ code: it.code, ligne: it.l }));
        if (erreurs.length) payload.contenu = { ...payload.contenu, erreurs };
        return payload;
    }
    throw new Error('gen_1120 : V/F rappel sans tirage après 160 essais');
};

// ─── Registre ──────────────────────────────────────────────────────────────

const GENERATORS = {
    quelle_qcm_unique: genQuelle,
    axe_qcm_unique: genAxe,
    centre_qcm_unique: genCentre,
    vf_tableau_vf: genVf,
    rappel_tableau_vf: genRappelVf,
};

const FORMAT_LABELS = { qcm_unique: 'QCM', tableau_vf: 'Vrai/Faux' };

const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter((k) => k.startsWith(`${prefix}_`))
        .map((k) => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'quelle', label: L_QUELLE, formatsList: buildFormats('quelle') },
    { id: 'axe', label: L_AXE, formatsList: buildFormats('axe') },
    { id: 'centre', label: L_CENTRE, formatsList: buildFormats('centre') },
    { id: 'vf', label: L_VF, formatsList: buildFormats('vf') },
    { id: 'rappel', label: L_RAPPEL, formatsList: buildFormats('rappel') },
];

// Codes retirés le 03/10/2026 après la lecture à l'aveugle (docs/kit/REGLES_EQUIPE.md) : le nom ne
// décrit pas le choix construit, ou deux erreurs donnent le même nombre, ou le choix est une figure
// que la lecture n'a pas pu vérifier. Le choix reste proposé, il part sans nom.
const CODES_RETIRES = {
    axe_qcm_unique: ['axe_ou_centre_decale'],
    centre_qcm_unique: ['axe_ou_centre_decale', 'axe_ou_centre_dans_la_figure'],
};

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    retirerCodesErreur(payload, CODES_RETIRES[key]);
    payload._meta.generatorKey = key;
    return payload;
};
